import React from 'react';
import { UserIcon } from '@heroicons/react/24/outline';
import DataTable, { type TableColumn } from '../common/DataTable';
import { type IEventAttendee, type IEventMilestone, type IMilestoneProgress } from '../../reducers/eventsSlice';
import EventAttendeeProgressIcon from './EventAttendeeProgressIcon';

interface Props {
  eventMilestones: IEventMilestone[];
  eventAttendees: IEventAttendee[] | null;
  nextMilestoneId: number | null;
}

export default function EventAttendeeProgressTable({ eventMilestones, eventAttendees, nextMilestoneId }: Props) {
  if (eventAttendees == null) {
    return <div>Loading...</div>;
  }

  const findLatestCompletedMilestone = (attendeeMilestones: IMilestoneProgress[]): string => {
    if (attendeeMilestones.length === 0) {
      return '';
    }

    const latestMilestone = attendeeMilestones.reduce((milestone1, milestone2) => {
      if (!milestone1 || new Date(milestone2.achieved_by) > new Date(milestone1.achieved_by)) {
        return milestone2;
      }
      return milestone1;
    }, attendeeMilestones[0]);

    return eventMilestones.find((milestone) => milestone.id === latestMilestone.event_milestone_id)?.title ?? '';
  };

  const columns: Array<TableColumn<IEventAttendee>> = [
    {
      key: 'name',
      label: 'Attendee',
      sortable: true,
      render: (attendee) => (
        <div className="flex items-center gap-x-4">
          <UserIcon className="w-5 h-5" />
          <div>
            <p className="font-semibold text-gray-900">{attendee.name}</p>
            <p className="text-gray-500 text-xs">{attendee.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'account_identifier',
      label: 'Account',
      sortable: true,
      render: (attendee) => (
        <div className="flex items-center space-x-1">
          <div>
            <a href={attendee.account_url} className="text-dataops-yale-blue hover:underline">
              {attendee.account_identifier}
            </a>
            <p className="text-gray-500 text-xs">At {attendee.allocated_at}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'progress',
      label: 'Progress',
      sortable: true,
      className: 'text-right',
      render: (attendee) => (
        <div className="flex items-center space-x-2">
          {eventMilestones.map((milestone, index) => (
            <EventAttendeeProgressIcon
              key={index}
              attendee={attendee}
              milestone={milestone}
              isNextMilestone={milestone.id === nextMilestoneId}
            />
          ))}
        </div>
      ),
    },
    {
      key: 'lastMilestone',
      label: 'Last Completed Milestone',
      className: 'text-right',
      render: (attendee) => (
        <div className="flex items-center space-x-1">
          <p className="pl-3 font-semibold text-gray-700">{findLatestCompletedMilestone(attendee.milestones)}</p>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      data={eventAttendees}
      columns={columns}
      keyExtractor={(attendee) => attendee.email}
      sortable={true}
      defaultSortField="name"
      defaultSortDirection="asc"
      emptyMessage="No attendees found"
    />
  );
}
